import { apiService } from './api';
import { toast } from '../components/notifications';
import { getWebSocketBaseUrl } from '../utils/websocket';

// Hilfsfunktion um zu prüfen, ob eine Session-ID lokal ist
const isLocalSession = (sessionId) => {
  return sessionId && typeof sessionId === 'string' && sessionId.startsWith('local-');
};

/**
 * Verwaltet die aktuelle Lernsession, Fokuszeiten und Herzschläge
 */
class SessionManager {
  constructor() {
    this.currentSession = null;
    this.focusStartTime = null;
    this.heartbeatInterval = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10; // Increased for better resilience
    this.baseReconnectDelay = 1000; // Base delay: 1 second
    this.maxReconnectDelay = 30000; // Max delay: 30 seconds
    this.reconnectBackoffFactor = 1.5; // Exponential backoff factor
    this.websocket = null;
    this.participants = [];
    this.sessionListeners = [];
    this.wsReconnectTimeout = null;
    this.isFocusTrackingActive = false;

    // CRITICAL: Connection health monitoring
    this.connectionHealthInterval = null;
    this.lastSuccessfulHeartbeat = null;
    this.consecutiveFailures = 0;
    this.isReconnecting = false;
    this.connectionState = 'disconnected'; // 'connected', 'connecting', 'disconnected', 'failed'
    this.lastConnectTime = 0; // Prevent reconnect storms

    // Bereinige zuerst alte/ungültige Sessions
    this.cleanupInvalidSessions();

    // Beim Laden der Anwendung prüfen, ob eine aktive Session existiert
    this.checkForExistingSession();

    // Nutzerwechsel ohne Reload (Login/Registrierung auf demselben Geraet, siehe
    // utils/auth.js handleUserSwitch): Die laufende Session gehoert dem VORHERIGEN
    // Nutzer. Ohne diesen Listener feuerte der Heartbeat mit dem neuen Token weiter
    // gegen die fremde Session -> 404 "Session nicht gefunden" als Toast mitten im
    // Onboarding (Prod-Trace 2. Sep 2026, 11:16-11:17 UTC).
    window.addEventListener('auth:userChanged', () => {
      if (this.currentSession) {
        console.warn('Nutzerwechsel erkannt, verwerfe Session des vorherigen Nutzers');
        this.cleanupSession();
      } else {
        this.stopHeartbeat();
        localStorage.removeItem('currentSession');
      }
    });

    // Event-Listener für Fenster-Fokus und Blur
    window.addEventListener('focus', this.handleWindowFocus.bind(this));
    window.addEventListener('blur', this.handleWindowBlur.bind(this));

    // Event-Listener für Seiten-Reload oder Schließen
    window.addEventListener('beforeunload', this.handlePageUnload.bind(this));
  }

  /**
   * Überprüft und bereinigt ungültige Session-Daten im localStorage
   */
  cleanupInvalidSessions() {
    try {
      // Prüfe auf ungültige Session-ID-Formate
      const savedSession = localStorage.getItem('currentSession');
      if (savedSession) {
        try {
          const sessionData = JSON.parse(savedSession);

          // Prüfe auf fehlende oder ungültige ID
          if (!sessionData || !sessionData.id) {
            console.warn('Session ohne ID gefunden, wird entfernt');
            localStorage.removeItem('currentSession');
            return;
          }

          // Prüfe bei Remote-Sessions auf fehlendes Token
          if (!isLocalSession(sessionData.id)) {
            const token = localStorage.getItem('token');
            if (!token) {
              console.warn('Remote-Session ohne Token gefunden, wird entfernt');
              localStorage.removeItem('currentSession');
              return;
            }
          }

          // Prüfe, ob die Session zu alt ist (älter als 4 Stunden)
          const sessionAge = Date.now() - (sessionData.joined || 0);
          const maxSessionAge = 4 * 60 * 60 * 1000; // 4 Stunden

          if (sessionAge > maxSessionAge) {
            console.warn('Alte Session gefunden, wird entfernt');
            localStorage.removeItem('currentSession');
            return;
          }
        } catch (error) {
          console.error('Ungültiges Session-Format im localStorage, wird bereinigt:', error);
          localStorage.removeItem('currentSession');
        }
      }
    } catch (error) {
      console.error('Fehler beim Bereinigen alter Sessions:', error);
    }
  }

  /**
   * Prüft auf eine existierende Session im localStorage
   */
  checkForExistingSession() {
    try {
      const savedSession = localStorage.getItem('currentSession');
      if (savedSession) {
        // Prüfe, ob die Session-Daten ein gültiges JSON-Format haben
        let sessionData;
        try {
          sessionData = JSON.parse(savedSession);
        } catch (parseError) {
          console.error('Ungültiges JSON-Format der gespeicherten Session:', parseError);
          this.cleanupSession();
          return;
        }

        // Prüfe, ob grundlegende Session-Eigenschaften vorhanden sind
        if (!sessionData.id || !sessionData.joined) {
          console.error('Ungültige Session-Struktur:', sessionData);
          this.cleanupSession();
          return;
        }

        // Gehoert die gespeicherte Session einem anderen Nutzer (Geraet wurde ohne
        // Logout weitergegeben)? Dann nicht wiederherstellen - sonst laufen Heartbeats
        // gegen eine fremde Session, die der Server mit 404 beantwortet.
        const currentUserId = localStorage.getItem('userId');
        if (sessionData.userId && currentUserId && sessionData.userId !== currentUserId) {
          console.warn('Gespeicherte Session gehoert einem anderen Nutzer, wird verworfen');
          this.cleanupSession();
          return;
        }

        // Prüfen, ob die Session noch gültig ist (nicht älter als 3 Stunden)
        const sessionAge = Date.now() - sessionData.joined;
        const maxSessionAge = 3 * 60 * 60 * 1000; // 3 Stunden

        if (sessionAge < maxSessionAge) {
          // Prüfe vor dem Initialisieren, ob es sich um eine lokale Session handelt
          if (isLocalSession(sessionData.id)) {
            console.log('Lokale Session gefunden:', sessionData.id);
            this.initSession(sessionData);
          } else {
            // Für Remote-Sessions: Prüfe, ob ein gültiger Token vorhanden ist
            const token = localStorage.getItem('token');
            if (!token) {
              console.warn('Keine Remote-Session ohne Token möglich, Session wird entfernt');
              this.cleanupSession();
              return;
            }

            // CRITICAL: Validate session actually exists on server before initializing
            // This prevents running WebSocket with stale sessions
            console.log('Validating remote session with server:', sessionData.id);

            // Don't initialize session immediately - wait for async validation
            // We'll do this asynchronously without blocking
            apiService.getSessionDetails(sessionData.id)
              .then(response => {
                if (response.success && response.data) {
                  const serverSession = response.data;

                  if (serverSession.status === 'ACTIVE' || serverSession.status === 'RUNNING') {
                    console.log('✅ Valid active session found on server:', sessionData.id);
                    const updatedSessionData = { ...sessionData, ...serverSession };
                    localStorage.setItem('currentSession', JSON.stringify(updatedSessionData));
                    this.initSession(updatedSessionData);
                  } else {
                    console.warn('❌ Session is no longer active on server:', serverSession.status);
                    this.cleanupSession();
                  }
                } else {
                  console.warn('❌ Session not found on server, cleaning up');
                  this.cleanupSession();
                }
              })
              .catch(error => {
                console.error('Failed to validate session with server:', error);
                if (error.response && error.response.status === 404) {
                  console.warn('❌ Session no longer exists on server (404), cleaning up');
                } else {
                  console.warn('❌ Error validating session, cleaning up to prevent stale data');
                }
                this.cleanupSession();
              });

            // Return early - session initialization will happen in the promise callback
            return;
          }
        } else {
          // Abgelaufene Session löschen
          console.log('Abgelaufene Session gefunden und entfernt');
          this.cleanupSession();
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der gespeicherten Session:', error);
      this.cleanupSession();
    }
  }

  /**
   * Initialisiert eine Session mit den gegebenen Daten
   * @param {Object} sessionData - Daten der Session
   */
  initSession(sessionData) {
    this.currentSession = sessionData;

    // WebSocket-Verbindung herstellen
    this.connectWebSocket();

    // Heartbeat starten
    this.startHeartbeat();

    console.log('Session initialisiert:', sessionData.id);

    // Event auslösen
    this.notifyListeners('sessionStart', sessionData);
  }

  /**
   * Uebernimmt eine Session, die laut Server gerade laeuft (App-Start, siehe
   * useSessionManager.checkServerSession). Wie bei createSession und beim Restore in
   * checkForExistingSession landet sie im localStorage: Der Heartbeat-Intervall prueft
   * dort bei jedem Tick, ob die Session noch dieselbe ist, und stoppt sonst still.
   * Ohne diesen Eintrag lief nach dem App-Start genau ein Heartbeat, danach nur noch
   * einzelne bei Fenster-Fokus - die presenceIntervals (Forschungsdaten) liessen
   * sich so nicht sauber fortschreiben (Audit A26).
   * @param {Object} serverSession - Session aus GET /sessions/my-active-session
   * @returns {Object} - Die uebernommene Session inkl. joined/userId
   */
  adoptServerSession(serverSession) {
    const sessionWithMeta = {
      ...serverSession,
      joined: Date.now(),
      userId: localStorage.getItem('userId') || undefined
    };

    try {
      localStorage.setItem('currentSession', JSON.stringify(sessionWithMeta));
    } catch (storageError) {
      console.warn('currentSession konnte nicht persistiert werden:', storageError);
    }

    this.initSession(sessionWithMeta);
    return sessionWithMeta;
  }

  /**
   * Erstellt eine neue Lernsession
   * @param {Object} sessionOptions - Optionen für die Session
   * @returns {Promise<Object>} - Die erstellte Session
   */
  async createSession(sessionOptions) {
    try {
      console.log('SessionManager.createSession aufgerufen mit:', sessionOptions);

      // Prüfe auf fehlende wichtige Felder
      if (!sessionOptions.topic || !sessionOptions.topic.trim()) {
        throw new Error("Bitte gib ein Thema für die Session an");
      }

      if (!sessionOptions.location || !sessionOptions.location.trim()) {
        throw new Error("Bitte gib einen Ort für die Session an");
      }

      // Lokale Mock-Session erstellen, falls API nicht antworten sollte
      const mockSession = {
        id: `local-${Date.now()}`,
        topic: sessionOptions.topic,
        location: sessionOptions.location,
        type: sessionOptions.type || 'PUBLIC',
        status: sessionOptions.status || 'ACTIVE',
        startTime: sessionOptions.startTime || new Date().toISOString(),
        hostId: sessionOptions.hostId,
        maxParticipants: sessionOptions.maxParticipants || 2,
        participants: [
          { id: sessionOptions.hostId, status: 'VALIDATED' }
        ],
        verificationCode: Math.random().toString(36).substr(2, 6).toUpperCase(),
        joined: Date.now(),
        isLocalFallback: true
      };

      // Versuche, die Session über die API zu erstellen
      let session;
      try {
        session = await apiService.createSession(sessionOptions);
        console.log('Session über API erstellt:', session);
      } catch (error) {
        console.warn('Fehler bei API-Anfrage, verwende lokalen Fallback:', error.message);
        // Im Produktionsmodus oder für echte Benutzer Fehler werfen
        if (process.env.NODE_ENV === 'production' && !sessionOptions.hostId.toString().startsWith('demo')) {
          throw error;
        }

        // Für Entwicklung/Demo Fallback verwenden
        console.log('Verwende lokalen Fallback für Session-Erstellung');
        session = mockSession;
      }

      // Session-Daten lokal speichern (userId: Eigentuemer-Check in checkForExistingSession)
      const sessionWithMeta = {
        ...session,
        joined: Date.now(),
        userId: localStorage.getItem('userId') || undefined
      };

      localStorage.setItem('currentSession', JSON.stringify(sessionWithMeta));

      // Session initialisieren
      this.initSession(sessionWithMeta);

      return sessionWithMeta;

    } catch (error) {
      console.error('Fehler beim Erstellen der Session:', error);
      throw error;
    }
  }

  /**
   * Stellt eine WebSocket-Verbindung für Echtzeit-Updates her
   */
  connectWebSocket() {
    if (!this.currentSession) return;

    // Keine WebSocket-Verbindung für lokale Sessions
    if (isLocalSession(this.currentSession.id)) {
      console.log('Lokale Session - keine WebSocket-Verbindung erforderlich');
      return;
    }

    // CRITICAL: Prevent reconnect storms - minimum 2s between connection attempts
    const now = Date.now();
    const timeSinceLastConnect = now - this.lastConnectTime;
    if (timeSinceLastConnect < 2000) {
      console.warn(`WebSocket connect throttled (${timeSinceLastConnect}ms since last attempt)`);
      return;
    }
    this.lastConnectTime = now;

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Kein Auth-Token für WebSocket-Verbindung vorhanden');
      // Bei Nicht-Demo-Sessions Aufräumen
      if (!this.currentSession.id.toString().includes('demo')) {
        this.cleanupSession();
      }
      return;
    }

    // Prüfe die WebSocket-Support im Browser
    if (typeof WebSocket === 'undefined') {
      console.error('WebSocket wird von diesem Browser nicht unterstützt');
      return;
    }

    // Bestehende WebSocket schließen, falls vorhanden
    if (this.websocket) {
      try {
        // CRITICAL: Remove event handlers BEFORE closing to prevent reconnect loop
        const oldWs = this.websocket;
        this.websocket = null; // Clear reference first
        
        oldWs.onopen = null;
        oldWs.onmessage = null;
        oldWs.onerror = null;
        oldWs.onclose = null;
        
        oldWs.close(1000, 'Reconnecting');
      } catch (error) {
        console.warn('Fehler beim Schließen der bestehenden WebSocket:', error);
      }
    }

    // WebSocket-URL für die aktuelle Session (funktioniert in Browser + Native App)
    const base = getWebSocketBaseUrl();
    const wsUrl = `${base}/api/ws/sessions/${this.currentSession.id}?token=${token}`;

    console.log('Versuche WebSocket-Verbindung herzustellen zu:', wsUrl.replace(/token=([^&]+)/, 'token=***'));

    try {
      this.websocket = new WebSocket(wsUrl);

      // Timeout für die WebSocket-Verbindung
      const connectionTimeout = setTimeout(() => {
        if (this.websocket && this.websocket.readyState !== WebSocket.OPEN) {
          console.error('WebSocket-Verbindungs-Timeout');
          this.websocket.close();

          // Prüfe, ob die Session möglicherweise ungültig ist
          if (this.reconnectAttempts >= 2) {
            console.warn('Mehrere WebSocket-Verbindungsversuche fehlgeschlagen, Session könnte ungültig sein');

            // Bei zu vielen fehlgeschlagenen Versuchen die Session als ungültig betrachten
            if (this.reconnectAttempts >= this.maxReconnectAttempts - 1) {
              console.error('Maximale Anzahl von Wiederverbindungsversuchen erreicht, Session wird entfernt');
              this.cleanupSession();
              return;
            }
          }
        }
      }, 10000); // 10 Sekunden Timeout

      // CRITICAL: Enhanced WebSocket event handlers with connection health
      this.websocket.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket-Verbindung hergestellt');

        // Reset connection health tracking
        this.reconnectAttempts = 0;
        this.consecutiveFailures = 0;
        this.isReconnecting = false;
        this.connectionState = 'connected';
        this.lastSuccessfulHeartbeat = Date.now();

        // Send reconnection notification to server
        if (this.websocket.readyState === WebSocket.OPEN) {
          try {
            this.websocket.send(JSON.stringify({
              type: 'CLIENT_RECONNECTED',
              data: {
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                sessionId: this.currentSession?.id
              }
            }));
          } catch (error) {
            console.warn('Failed to send reconnection notification:', error);
          }
        }

        // Start connection health monitoring
        this.startConnectionHealthMonitoring();

        this.notifyListeners('websocketConnected', {
          reconnectAttempts: this.reconnectAttempts,
          wasReconnection: this.reconnectAttempts > 0
        });
      };

      this.websocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleWebSocketMessage(message);
        } catch (error) {
          console.error('Fehler beim Verarbeiten der WebSocket-Nachricht:', error);
        }
      };

      this.websocket.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket-Verbindung geschlossen', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          reconnectAttempts: this.reconnectAttempts
        });

        // Stop connection health monitoring
        this.stopConnectionHealthMonitoring();
        this.connectionState = 'disconnected';

        this.notifyListeners('websocketDisconnected', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });

        // CRITICAL: Enhanced reconnection logic
        const shouldReconnect = this.shouldAttemptReconnection(event);

        if (shouldReconnect && this.currentSession) {
          console.log('Scheduling reconnection due to connection loss');
          this.scheduleReconnect();
        } else {
          const reason = this.getDisconnectionReason(event);
          console.warn('Not attempting reconnection:', reason);

          if (event.code === 1008 || event.code === 1003 || this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.cleanupSession();
          }
        }
      };

      this.websocket.onerror = (error) => {
        console.warn('WebSocket-Fehler:', error);
        this.consecutiveFailures++;

        // If too many consecutive failures, clean up session
        if (this.consecutiveFailures >= 5) {
          console.error('Too many consecutive WebSocket failures, cleaning up session');
          this.cleanupSession();
        }
      };


    } catch (error) {
      console.error('Fehler beim Herstellen der WebSocket-Verbindung:', error);

      // Bei schwerwiegenden Verbindungsfehlern die Session überprüfen
      if (this.reconnectAttempts >= 2) {
        console.warn('Kritischer WebSocket-Verbindungsfehler, Session wird entfernt');
        this.cleanupSession();
      }
    }
  }

  /**
   * CRITICAL: Enhanced reconnection with exponential backoff
   */
  scheduleReconnect() {
    if (this.wsReconnectTimeout) {
      clearTimeout(this.wsReconnectTimeout);
    }

    // Don't attempt reconnection if we're already at max attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached, giving up');
      this.connectionState = 'failed';
      this.notifyListeners('connectionFailed', {
        reason: 'Max reconnection attempts reached',
        attempts: this.reconnectAttempts
      });
      return;
    }

    this.isReconnecting = true;
    this.connectionState = 'connecting';

    // CRITICAL: Exponential backoff with jitter to prevent thundering herd
    const baseDelay = this.baseReconnectDelay * Math.pow(this.reconnectBackoffFactor, this.reconnectAttempts);
    const cappedDelay = Math.min(baseDelay, this.maxReconnectDelay);

    // Add jitter (±25%) to prevent all clients reconnecting simultaneously
    const jitter = cappedDelay * 0.25 * (Math.random() - 0.5);
    const finalDelay = Math.max(cappedDelay + jitter, this.baseReconnectDelay);

    console.log(`Scheduling WebSocket reconnection attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts} in ${Math.round(finalDelay)}ms`);

    this.wsReconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.notifyListeners('reconnectAttempt', {
        attempt: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts,
        delay: finalDelay
      });
      this.connectWebSocket();
    }, finalDelay);
  }

  /**
   * Verarbeitet eingehende WebSocket-Nachrichten
   * @param {Object} message - Die empfangene Nachricht
   */
  handleWebSocketMessage(message) {
    console.log('WebSocket-Nachricht empfangen:', message.type);

    switch (message.type) {
      case 'PARTICIPANT_JOINED':
        this.handleParticipantJoined(message.data);
        break;
      case 'PARTICIPANT_LEFT':
        this.handleParticipantLeft(message.data);
        break;
      case 'PARTICIPANT_KICKED':
        this.handleParticipantKicked(message.data);
        break;
      case 'SESSION_PARTICIPANTS':
        // Update participants list from WebSocket
        if (message.data && message.data.participants) {
          this.participants = message.data.participants;
          this.notifyListeners('participantsUpdated', message.data);
          console.log('Participants updated via WebSocket:', this.participants.length);
        }
        break;
      case 'FOCUS_UPDATE':
        this.handleFocusUpdate(message.data);
        break;
      case 'SESSION_COMPLETED':
        this.handleSessionCompleted(message.data);
        break;
      case 'SESSION_CANCELLED':
        this.handleSessionCancelled(message.data);
        break;
      case 'BONUS_AWARDED':
        this.handleBonusAwarded(message.data);
        break;
      case 'SESSION_STATE_SYNC':
        // Server sends full state on reconnect - update participants
        if (message.data && message.data.participants) {
          this.participants = message.data.participants;
          this.notifyListeners('participantsUpdated', message.data);
          console.log('Session state synced from server:', this.participants.length, 'participants');
        }
        break;
      case 'PARTICIPANT_OFFLINE':
        // Participant temporarily disconnected but still in session
        this.notifyListeners('participantOffline', message.data);
        break;
      case 'PARTICIPANT_RECONNECTED':
        // Participant came back online
        this.notifyListeners('participantReconnected', message.data);
        break;
      case 'SESSION_UPDATE':
        // Session data changed (e.g. status update)
        this.notifyListeners('sessionUpdate', message.data);
        break;
      case 'HEARTBEAT_ACK':
        // Server acknowledged heartbeat - update health
        this.lastSuccessfulHeartbeat = Date.now();
        break;
      default:
        console.log('Unbekannter Nachrichtentyp:', message.type);
    }

    // Event auslösen
    this.notifyListeners('websocketMessage', message);
  }

  /**
   * Haelt currentSession.participants (Quelle des ActiveSessionBanner-Zaehlers)
   * bei Join/Leave/Kick aktuell. Bisher mutierten die WS-Handler nur
   * this.participants - das Banner las den eingefrorenen Stand vom Join-Zeitpunkt.
   *
   * Bewusst NUR inkrementell (add/remove einzelner userIds), NICHT die komplette
   * Liste aus SESSION_PARTICIPANTS uebernehmen: die ist WS-Raum-Mitgliedschaft
   * (nur gerade verbundene Clients) und wuerde die Teilnehmerzahl untertreiben.
   *
   * WICHTIG: neues Objekt erzeugen statt mutieren - useSessionManager vergleicht
   * per Referenz und wuerde bei Mutation nicht re-rendern.
   * @param {Function} updateParticipants - bekommt die bisherige Liste, liefert die neue
   */
  _updateCurrentSessionParticipants(updateParticipants) {
    if (!this.currentSession) return;
    const previous = Array.isArray(this.currentSession.participants)
      ? this.currentSession.participants
      : [];
    this.currentSession = {
      ...this.currentSession,
      participants: updateParticipants(previous),
    };
    try {
      localStorage.setItem('currentSession', JSON.stringify(this.currentSession));
    } catch (storageError) {
      console.warn('currentSession konnte nicht persistiert werden:', storageError);
    }
  }

  /**
   * Verarbeitet das Beitreten eines neuen Teilnehmers
   * @param {Object} data - Teilnehmer-Daten
   */
  handleParticipantJoined(data) {
    // Teilnehmer zur Liste hinzufügen, wenn noch nicht vorhanden
    const exists = this.participants.some(p => p.userId === data.userId);
    if (!exists) {
      this.participants.push(data);
    }

    this._updateCurrentSessionParticipants(previous => (
      previous.some(p => String(p.userId) === String(data.userId))
        ? previous
        : [...previous, data.participant || { userId: data.userId }]
    ));

    this.notifyListeners('participantJoined', data);
  }

  /**
   * Verarbeitet das Verlassen eines Teilnehmers
   * @param {Object} data - Teilnehmer-Daten
   */
  handleParticipantLeft(data) {
    // Teilnehmer aus der Liste entfernen
    this.participants = this.participants.filter(p => p.userId !== data.userId);

    this._updateCurrentSessionParticipants(previous =>
      previous.filter(p => String(p.userId) !== String(data.userId)));

    this.notifyListeners('participantLeft', data);
  }

  /**
   * Verarbeitet den Kick eines Teilnehmers durch den Host.
   * Ob es "mich" trifft (Toast + Navigation), entscheidet die Seiten-Ebene
   * (SessionDetailPage) - der SessionManager kennt die eigene userId nicht.
   * @param {Object} data - { sessionId, userId, kickedBy, reason }
   */
  handleParticipantKicked(data) {
    this.participants = this.participants.filter(p => p.userId !== data.userId);

    this._updateCurrentSessionParticipants(previous =>
      previous.filter(p => String(p.userId) !== String(data.userId)));

    this.notifyListeners('participantKicked', data);
  }

  /**
   * Verarbeitet Fokuszeit-Updates
   * @param {Object} data - Fokuszeit-Daten
   */
  handleFocusUpdate(data) {
    // Fokuszeiten der Teilnehmer aktualisieren
    const participant = this.participants.find(p => p.userId === data.userId);
    if (participant) {
      participant.focusTime = data.focusTime;
    }

    this.notifyListeners('focusUpdate', data);
  }

  /**
   * Gehört ein Abschluss-/Abbruch-Event zu einer anderen als der aktuellen Session?
   * Nach einem Sessionwechsel konnte der Server Events der alten Session noch auf die
   * neue Verbindung schicken - sie beendeten dann die laufende Session.
   * Events ohne sessionId (ältere Server) werden wie bisher verarbeitet.
   * @param {Object} data - Event-Daten
   * @returns {boolean}
   */
  isForeignSessionEvent(data) {
    const eventSessionId = data?.sessionId;
    const currentSessionId = this.currentSession?.id;
    return Boolean(eventSessionId && currentSessionId && String(eventSessionId) !== String(currentSessionId));
  }

  /**
   * Verarbeitet das Abschließen einer Session (durch den Host)
   * CRITICAL: Alle Teilnehmer müssen darüber benachrichtigt werden!
   * @param {Object} data - Abschluss-Daten { sessionId, duration, topic, hostId, completedAt, ... }
   */
  handleSessionCompleted(data) {
    if (this.isForeignSessionEvent(data)) {
      console.warn('SESSION_COMPLETED einer anderen Session ignoriert:', data.sessionId);
      return;
    }

    console.log('📡 SESSION_COMPLETED Event empfangen:', {
      sessionId: data.sessionId,
      topic: data.topic,
      duration: data.duration,
      hostId: data.hostId
    });

    // Session als beendet markieren
    if (this.currentSession) {
      this.currentSession.completed = true;
      this.currentSession.status = 'COMPLETED';
      this.currentSession.completedAt = data.completedAt || data.timestamp || new Date().toISOString();

      // In localStorage aktualisieren
      localStorage.setItem('currentSession', JSON.stringify(this.currentSession));
    }

    // Heartbeat stoppen
    this.stopHeartbeat();

    // Benachrichtige alle Listener mit vollständigen Daten
    // Die UI-Komponenten (SessionStatus, SessionDetailPage) reagieren darauf
    this.notifyListeners('sessionCompleted', {
      ...data,
      message: `Die Session "${data.topic || 'Lernsession'}" wurde beendet.`,
      shouldShowFeedback: true // Signal für UI: Feedback-Formular anzeigen
    });
  }

  /**
   * Verarbeitet das Abbrechen einer Session (durch den Host)
   * Alle Teilnehmer werden benachrichtigt und zur Startseite geleitet
   * @param {Object} data - Abbruch-Daten { sessionId, cancelledBy, reason, sessionTopic }
   */
  handleSessionCancelled(data) {
    if (this.isForeignSessionEvent(data)) {
      console.warn('SESSION_CANCELLED einer anderen Session ignoriert:', data.sessionId);
      return;
    }

    console.log('Session wurde abgebrochen:', data);

    // Session-Daten speichern für Benachrichtigung
    const sessionTopic = data.sessionTopic || this.currentSession?.topic || 'Lernsession';
    const reason = data.reason || 'Session wurde vom Host abgebrochen';

    // Heartbeat und WebSocket stoppen
    this.stopHeartbeat();
    this.stopConnectionHealthMonitoring();

    // Session-Daten bereinigen
    if (this.currentSession) {
      this.currentSession.cancelled = true;
      this.currentSession.cancelledAt = data.timestamp;
    }

    // Local Storage bereinigen
    localStorage.removeItem('currentSession');

    // Benachrichtige alle Listener über den Abbruch
    // Die UI-Komponenten (MainScreen, SessionStatus) reagieren darauf
    this.notifyListeners('sessionCancelled', {
      ...data,
      sessionTopic,
      reason,
      message: `Die Session "${sessionTopic}" wurde abgebrochen.`
    });

    // Session-Daten zurücksetzen
    this.currentSession = null;
    this.participants = [];

    // WebSocket schließen
    if (this.websocket) {
      this.websocket.close(1000, 'Session cancelled');
      this.websocket = null;
    }
  }

  /**
   * Verarbeitet die Vergabe eines Team-Bonus
   * @param {Object} data - Bonus-Daten
   */
  handleBonusAwarded(data) {
    this.notifyListeners('bonusAwarded', data);
  }

  /**
   * CRITICAL: Enhanced heartbeat with connection health monitoring
   */
  startHeartbeat() {
    if (!this.currentSession) {
      console.log('Kein Heartbeat ohne aktive Session');
      return;
    }

    this.stopHeartbeat();

    const isLocal = isLocalSession(this.currentSession.id);
    const isDemo = this.currentSession.id.includes('demo');

    if (!isLocal && !isDemo) {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('Keine Heartbeats ohne Token für Remote-Sessions');
        this.cleanupSession();
        return;
      }
    }

    if (!this.currentSession.id) {
      console.error('Session ohne ID, kann keinen Heartbeat senden');
      this.cleanupSession();
      return;
    }

    console.log(`Starte Heartbeat für Session: ${this.currentSession.id}`);

    // CRITICAL: Adaptive heartbeat interval based on connection health
    const baseInterval = isLocal || isDemo ? 60000 : 30000;
    const heartbeatInterval = this.getAdaptiveHeartbeatInterval(baseInterval);

    // Alle 30/60 Sekunden einen Heartbeat senden
    this.heartbeatInterval = setInterval(async () => {
      try {
        if (!this.currentSession) {
          this.stopHeartbeat();
          return;
        }

        // Prüfe, ob ein gültiger Token vorhanden ist
        const token = localStorage.getItem('token');
        if (!token && !isLocalSession(this.currentSession.id)) {
          console.warn('Heartbeat-Intervall: Kein Token vorhanden');
          this.notifyListeners('authError', { message: 'Deine Sitzung ist abgelaufen' });
          this.stopHeartbeat();
          return;
        }

        // Prüfe, ob die Session im localStorage noch die gleiche ist
        // Dies kann wichtig sein, wenn z.B. durch andere Tabs Änderungen vorgenommen wurden
        const currentSessionJson = localStorage.getItem('currentSession');
        if (currentSessionJson) {
          try {
            const storedSession = JSON.parse(currentSessionJson);
            if (storedSession.id !== this.currentSession.id) {
              console.warn('Session-ID hat sich geändert, Heartbeat wird gestoppt');
              this.stopHeartbeat();
              return;
            }
          } catch (e) {
            console.error('Fehler beim Parsen der gespeicherten Session im Heartbeat:', e);
          }
        } else {
          // Keine Session mehr im localStorage
          console.warn('Keine Session mehr im localStorage, Heartbeat wird gestoppt');
          this.stopHeartbeat();
          return;
        }

        // Die Heartbeat-Antwort reicht als Lebenszeichen. Frueher folgte hier noch
        // POST /sessions/:id/activity - diese Route gab es im Backend nie. Ihr 404 lief
        // unten als SESSION_NOT_FOUND in cleanupSession() und beendete die Session im
        // Client nach spaetestens 30 s, obwohl sie auf dem Server weiterlief (Audit A26).
        const heartbeatData = await apiService.sessionHeartbeat(this.currentSession.id);

        // CRITICAL: Update connection health on successful heartbeat
        this.lastSuccessfulHeartbeat = Date.now();
        this.consecutiveFailures = 0;
        this.heartbeatErrors = 0;

        this.notifyListeners('heartbeat', heartbeatData);

      } catch (error) {
        console.error('Fehler beim Senden des Heartbeats:', error);

        // CRITICAL: Enhanced error handling with exponential backoff
        this.heartbeatErrors = (this.heartbeatErrors || 0) + 1;
        this.consecutiveFailures++;
        this.lastSuccessfulHeartbeat = null;

        const errorType = this.categorizeHeartbeatError(error);

        switch (errorType) {
          case 'AUTH_ERROR':
            console.warn('Authentifizierungsfehler beim Heartbeat');
            this.handleAuthError(error);
            break;
          case 'SESSION_NOT_FOUND':
            console.warn('Session nicht auf dem Server gefunden');
            this.cleanupSession();
            break;
          case 'SERVER_ERROR':
            console.warn('Server-Fehler beim Heartbeat');
            if (this.heartbeatErrors >= 5) {
              console.error('Zu viele Server-Fehler, Session wird entfernt');
              this.cleanupSession();
            }
            break;
          case 'NETWORK_ERROR':
            console.warn('Netzwerk-Fehler beim Heartbeat');
            // Don't immediately clean up for network errors - they might be temporary
            if (this.heartbeatErrors >= 10) {
              this.notifyListeners('connectionLost', { reason: 'network' });
            }
            break;
          default:
            console.warn('Unbekannter Heartbeat-Fehler:', error);
        }

        // CRITICAL: Adaptive heartbeat interval on errors
        if (this.heartbeatErrors >= 3 && this.heartbeatErrors < 10) {
          // Increase heartbeat interval on errors to reduce server load
          this.adaptHeartbeatInterval(true);
        }
      }
    }, heartbeatInterval);

    // Sofort ersten Heartbeat senden
    this.sendHeartbeat();
  }

  /**
   * Sendet sofort einen Heartbeat
   */
  async sendHeartbeat() {
    if (!this.currentSession) return;

    try {
      // Versuche, einen Context-Refresh für den Auth-Token durchzuführen
      // Dies erfolgt indirekt, da wir keinen direkten Zugriff auf den AuthContext haben
      const authRefreshEvent = new CustomEvent('auth:checkToken');
      window.dispatchEvent(authRefreshEvent);

      // Kurze Pause, um dem Auth-System Zeit zu geben
      await new Promise(resolve => setTimeout(resolve, 100));

      // Race-Condition Guard: Session könnte während des awaits auf null gesetzt worden sein (v.a. iOS)
      if (!this.currentSession) return;

      // Prüfe zuerst, ob ein Token vorhanden ist
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('Heartbeat kann nicht gesendet werden: Kein Token vorhanden');

        // Bei lokalen Sessions ohne Token trotzdem weitermachen
        if (isLocalSession(this.currentSession.id)) {
          console.log('Lokale Session erkannt, setze Heartbeat fort trotz fehlendem Token');
        } else {
          // Kein Token (z.B. nach Logout / auf der Login-Seite) ist KEIN echter
          // Fehler. Frueher wurde hier geworfen -> der catch-Block unten meldete
          // das per console.error nach Sentry (Issue JAVASCRIPT-REACT-E7) und
          // stoppte den Heartbeat NICHT (error.response war undefined, daher griff
          // der 401-Guard nicht), sodass der Heartbeat weiter feuerte und den
          // Fehler wiederholt meldete. Wie im Intervall-Pfad: authError melden,
          // Heartbeat sauber stoppen und beenden.
          this.notifyListeners('authError', { message: 'Deine Sitzung ist abgelaufen' });
          this.stopHeartbeat();
          return;
        }
      }

      await apiService.sessionHeartbeat(this.currentSession.id);

      // CRITICAL: Update connection health tracking
      this.heartbeatErrors = 0;
      this.consecutiveFailures = 0;
      this.lastSuccessfulHeartbeat = Date.now();

      // Race-Condition Guard: Session könnte während des Heartbeat-awaits oben
      // auf null gesetzt worden sein (v.a. iOS beim Verlassen der Session).
      if (!this.currentSession) return;

      // Aktuelle Teilnehmer abrufen (Hintergrund: 403/404 raeumt der catch still auf)
      const participants = await apiService.getSessionParticipants(this.currentSession.id, { skipErrorToast: true });
      this.participants = participants;

      this.notifyListeners('participantsUpdated', participants);

    } catch (error) {
      console.error('Fehler beim Senden des manuellen Heartbeats:', error);

      // 404/403: Die Session existiert (fuer diesen Nutzer/diese Schule) nicht mehr.
      // Dieser manuelle Heartbeat laeuft bei jedem Fenster-Fokus - auf dem iPhone also
      // nach jedem App-Wechsel (Foto auswaehlen, Kamera ...). Ohne Aufraeumen feuerte er
      // endlos weiter gegen eine veraltete Session aus dem localStorage.
      const status = error.response?.status;
      if (status === 404 || status === 403) {
        console.warn(`Heartbeat: Session nicht (mehr) zugaenglich (${status}), wird entfernt`);
        this.cleanupSession();
        return;
      }

      // Bei Authentifizierungsfehlern nicht ständig versuchen, den Heartbeat zu senden
      if (error.response && error.response.status === 401) {
        console.warn('Authentifizierungsfehler beim Heartbeat. Stoppe Heartbeat-Intervall');
        this.stopHeartbeat();

        // Globales Auth-Error-Event auslösen für App-weite Fehlerbehandlung
        const authErrorEvent = new CustomEvent('auth:error', {
          detail: {
            message: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
            source: 'heartbeat'
          }
        });
        window.dispatchEvent(authErrorEvent);

        this.notifyListeners('authError', { message: 'Deine Sitzung ist abgelaufen' });
      }
    }
  }

  /**
   * Stoppt den Heartbeat-Mechanismus
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    this.heartbeatErrors = 0;
  }

  /**
   * Startet das Fokus-Tracking für die aktuelle Session
   * @returns {boolean} - Erfolg des Starts
   */
  startFocusTracking() {
    if (!this.currentSession) {
      console.error('Fokus-Tracking kann nicht gestartet werden: Keine aktive Session');
      return false;
    }

    this.isFocusTrackingActive = true;

    // Initialen Fokus-Status setzen basierend auf aktuellem Fensterfokus
    const isCurrentlyFocused = document.hasFocus();
    this.updateFocusStatus(isCurrentlyFocused);

    console.log('Fokus-Tracking gestartet für Session:', this.currentSession.id);
    this.notifyListeners('focusTrackingStarted', { isActive: true });

    return true;
  }

  /**
   * Stoppt das Fokus-Tracking für die aktuelle Session
   * @returns {boolean} - Erfolg des Stopps
   */
  stopFocusTracking() {
    if (!this.isFocusTrackingActive) {
      return false;
    }

    // Falls Fokus-Tracking aktiv ist, letzte Fokuszeit berechnen und senden
    if (this.focusStartTime) {
      this.updateFocusStatus(false);
    }

    this.isFocusTrackingActive = false;
    console.log('Fokus-Tracking gestoppt');
    this.notifyListeners('focusTrackingStopped', { isActive: false });

    return true;
  }

  /**
   * Aktualisiert den Fokuszeit-Status in der Session
   * @param {boolean} isFocused - Ob der Benutzer fokussiert ist
   * @returns {Promise<void>}
   */
  async updateFocusStatus(isFocused) {
    if (!this.currentSession || !this.isFocusTrackingActive) return;

    // Beim Betreten des Fokus-Modus Startzeit speichern
    if (isFocused) {
      this.focusStartTime = Date.now();
    }
    // Beim Verlassen des Fokus-Modus die akkumulierte Zeit berechnen und senden
    else if (this.focusStartTime) {
      const focusDuration = Date.now() - this.focusStartTime;

      try {
        await apiService.updateSessionFocus(this.currentSession.id, {
          focusTimeMs: focusDuration,
          endTime: new Date().toISOString()
        });

        // Fokuszeit-Update-Event auslösen
        this.notifyListeners('localFocusUpdate', {
          focusTimeMs: focusDuration,
          isFocused: false
        });

      } catch (error) {
        console.error('Fehler beim Aktualisieren der Fokuszeit:', error);
      }

      this.focusStartTime = null;
    }
  }

  /**
   * Schließt eine Session ab
   * @returns {Promise<Object>} - Daten der abgeschlossenen Session
   */
  async completeSession() {
    if (!this.currentSession) {
      throw new Error('Keine aktive Session vorhanden');
    }

    try {
      // Eventuell laufende Fokuszeit beenden
      if (this.focusStartTime) {
        await this.updateFocusStatus(false);
      }

      // Session beim Server als abgeschlossen markieren
      const completionData = await apiService.completeSession(this.currentSession.id);

      // Aufräumen
      this.cleanupSession();

      return completionData;

    } catch (error) {
      console.error('Fehler beim Abschließen der Session:', error);
      throw error;
    }
  }

  /**
   * Bricht eine Session ab (Host). Anders als completeSession() darf die
   * Session-ID explizit uebergeben werden: Der MainScreen kennt die aktive
   * Session aus seiner Liste, waehrend currentSession im Manager nach einem
   * Reload leer sein kann. Ohne diesen Aufruf blieb die Session auf dem Server
   * ACTIVE und tauchte nach dem naechsten Laden wieder auf.
   * @param {string} [sessionId] - Fallback: this.currentSession.id
   * @returns {Promise<Object>} - Antwort des Servers
   */
  async cancelSession(sessionId) {
    const targetId = sessionId || this.currentSession?.id;
    if (!targetId) {
      throw new Error('Keine Session zum Abbrechen vorhanden');
    }

    try {
      if (this.focusStartTime) {
        await this.updateFocusStatus(false);
      }

      // apiService.cancelSession invalidiert den Sessions-Cache selbst
      const result = await apiService.cancelSession(targetId);

      if (this.currentSession && String(this.currentSession.id) === String(targetId)) {
        this.cleanupSession();
      }

      return result;
    } catch (error) {
      console.error('Fehler beim Abbrechen der Session:', error);
      throw error;
    }
  }

  /**
   * Verlässt eine Session ohne sie abzuschließen
   * @returns {Promise<void>}
   */
  async leaveSession() {
    if (!this.currentSession) return;

    try {
      // Eventuell laufende Fokuszeit beenden
      if (this.focusStartTime) {
        await this.updateFocusStatus(false);
      }

      // Server über das Verlassen informieren
      await apiService.leaveSession(this.currentSession.id);

      // Aufräumen
      this.cleanupSession();

    } catch (error) {
      console.error('Fehler beim Verlassen der Session:', error);

      // Trotz Fehler aufräumen
      this.cleanupSession();

      throw error;
    }
  }

  /**
   * CRITICAL: Enhanced cleanup with memory leak prevention
   */
  cleanupSession() {
    console.log('Cleaning up session with comprehensive memory management');

    // Stop all intervals and timeouts
    this.stopHeartbeat();
    this.stopConnectionHealthMonitoring();

    if (this.wsReconnectTimeout) {
      clearTimeout(this.wsReconnectTimeout);
      this.wsReconnectTimeout = null;
    }

    // CRITICAL: Proper WebSocket cleanup
    if (this.websocket) {
      try {
        // Remove all event listeners to prevent memory leaks
        this.websocket.onopen = null;
        this.websocket.onclose = null;
        this.websocket.onmessage = null;
        this.websocket.onerror = null;

        // Close connection
        if (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING) {
          this.websocket.close(1000, 'Session cleanup');
        }
      } catch (error) {
        console.warn('Error during WebSocket cleanup:', error);
      } finally {
        this.websocket = null;
      }
    }

    // Reset connection state
    this.connectionState = 'disconnected';
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.consecutiveFailures = 0;
    this.heartbeatErrors = 0;
    this.lastSuccessfulHeartbeat = null;

    // Clear data
    localStorage.removeItem('currentSession');

    // Reset session data
    this.currentSession = null;
    this.focusStartTime = null;
    this.participants = [];

    this.notifyListeners('sessionEnd', { reason: 'cleanup' });
  }

  /**
   * Versucht, sich mit einer bestehenden Session zu verbinden
   * @param {string} sessionId - ID der Session
   * @returns {Promise<boolean>} - Erfolg der Wiederverbindung
   */
  async reconnectToSession(sessionId) {
    try {
      // Prüfen, ob die Session existiert und gültig ist
      const sessionData = await apiService.reconnectToSession(sessionId, {
        reconnectTime: new Date().toISOString()
      });

      // Session-Daten im localStorage aktualisieren (userId: Eigentuemer-Check beim Restore)
      const sessionWithMeta = {
        ...sessionData,
        joined: Date.now(),
        userId: localStorage.getItem('userId') || undefined
      };

      localStorage.setItem('currentSession', JSON.stringify(sessionWithMeta));

      // Session initialisieren
      this.initSession(sessionWithMeta);

      return true;

    } catch (error) {
      console.error('Fehler bei der Wiederverbindung zur Session:', error);
      return false;
    }
  }

  /**
   * Verarbeitet den Fokus des Browser-Fensters
   */
  handleWindowFocus() {
    // Aktualisiere Fokuszeit, wenn bereits in einer Session
    if (this.currentSession) {
      // WebSocket-Verbindung wieder herstellen, falls getrennt
      // connectWebSocket() has its own throttle (2s minimum between attempts)
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        if (!this.isReconnecting) {
          this.connectWebSocket();
        }
      }

      // Heartbeat aktualisieren
      this.sendHeartbeat();

      // Fokuszustand aktualisieren
      this.updateFocusStatus(true);
    }
  }

  /**
   * Verarbeitet den Verlust des Fokus des Browser-Fensters
   */
  handleWindowBlur() {
    // Fokuszeit aktualisieren, wenn in einer Session
    if (this.currentSession) {
      this.updateFocusStatus(false);
    }
  }

  /**
   * Verarbeitet das Neuladen oder Schließen der Seite
   * @param {Event} event - Das beforeunload-Event
   */
  handlePageUnload(event) {
    // Falls aktive Session vorhanden, Fokuszeit aktualisieren
    if (this.currentSession && this.focusStartTime) {
      // Synchroner API-Aufruf, da async nicht garantiert ist, dass der Request abgeschlossen wird
      const focusDuration = Date.now() - this.focusStartTime;

      // Navigator.sendBeacon für zuverlässigeres Senden beim Schließen verwenden
      if (navigator.sendBeacon) {
        const token = localStorage.getItem('token');

        if (token) {
          // Erstelle einen Blob mit Daten und Header
          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };

          const data = {
            focusTimeMs: focusDuration,
            endTime: new Date().toISOString()
          };

          // Die Headers können nicht direkt mit sendBeacon gesendet werden,
          // daher verwenden wir eine FormData, die die Headers und Daten enthält
          const formData = new FormData();
          formData.append('headers', JSON.stringify(headers));
          formData.append('data', JSON.stringify(data));

          // Sende den Request
          navigator.sendBeacon(
            `/api/v1/sessions/${this.currentSession.id}/focus`,
            formData
          );
        } else {
          console.error('Kein Token für sendBeacon vorhanden');
        }
      }
    }

    // Keine weitere Behandlung erforderlich
    // Die Session-Daten bleiben im localStorage und können beim nächsten Laden wiederhergestellt werden
  }

  /**
   * Registriert einen Listener für Session-Events
   * @param {Function} listener - Callback-Funktion für Events
   * @returns {Function} - Funktion zum Entfernen des Listeners
   */
  addEventListener(listener) {
    this.sessionListeners.push(listener);
    return () => {
      this.sessionListeners = this.sessionListeners.filter(l => l !== listener);
    };
  }

  /**
   * Benachrichtigt alle Listener über ein Event
   * @param {string} eventType - Typ des Events
   * @param {Object} data - Event-Daten
   */
  notifyListeners(eventType, data) {
    this.sessionListeners.forEach(listener => {
      try {
        listener(eventType, data);
      } catch (error) {
        console.error('Fehler in Session-Event-Listener:', error);
      }
    });
  }

  /**
   * Gibt die aktuelle Session zurück
   * @returns {Object|null} - Die aktuelle Session oder null
   */
  getCurrentSession() {
    return this.currentSession;
  }

  /**
   * Gibt die Liste der Teilnehmer zurück
   * @returns {Array} - Liste der Teilnehmer
   */
  getParticipants() {
    return [...this.participants];
  }

  /**
   * Prüft, ob eine aktive Session vorhanden ist
   * @returns {boolean}
   */
  hasActiveSession() {
    return this.currentSession !== null;
  }

  /**
   * Hilfsmethode zum manuellen Entfernen von ungültigen Sessions
   * Kann von außen aufgerufen werden, z.B. wenn ein Benutzer einen Fehler meldet
   */
  removeInvalidSession() {
    console.log('Entferne ungültige Session manuell');
    this.cleanupSession();

    // Damit der Benutzer weiß, dass die Aktion erfolgreich war
    toast?.success?.('Session erfolgreich zurückgesetzt', {
      position: "top-right",
      autoClose: 3000
    });

    // Seite aktualisieren, um sicherzustellen, dass alle Komponenten einen sauberen Zustand haben
    setTimeout(() => {
      window.location.reload();
    }, 1000);

    return true;
  }

  /**
   * CRITICAL: New methods for connection health and error handling
   */

  startConnectionHealthMonitoring() {
    if (this.connectionHealthInterval) {
      clearInterval(this.connectionHealthInterval);
    }

    this.connectionHealthInterval = setInterval(() => {
      this.checkConnectionHealth();
    }, 15000); // Check every 15 seconds
  }

  stopConnectionHealthMonitoring() {
    if (this.connectionHealthInterval) {
      clearInterval(this.connectionHealthInterval);
      this.connectionHealthInterval = null;
    }
  }

  checkConnectionHealth() {
    const now = Date.now();
    const timeSinceLastHeartbeat = this.lastSuccessfulHeartbeat ? now - this.lastSuccessfulHeartbeat : Infinity;

    // If no successful heartbeat in 2 minutes, consider connection unhealthy
    if (timeSinceLastHeartbeat > 120000) {
      console.warn('Connection appears unhealthy, last successful heartbeat:',
        this.lastSuccessfulHeartbeat ? new Date(this.lastSuccessfulHeartbeat).toISOString() : 'never');

      this.notifyListeners('connectionUnhealthy', {
        timeSinceLastHeartbeat,
        consecutiveFailures: this.consecutiveFailures
      });

      // Attempt WebSocket reconnection if connection seems dead
      if ((!this.websocket || this.websocket.readyState !== WebSocket.OPEN) && !this.isReconnecting) {
        console.log('Attempting to reconnect WebSocket due to unhealthy connection');
        this.connectWebSocket();
      }
    }
  }

  shouldAttemptReconnection(event) {
    // Don't reconnect if we've hit max attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return false;
    }

    // Don't reconnect on authentication errors
    if (event.code === 1008 || event.code === 1003) {
      return false;
    }

    // CRITICAL: Don't reconnect if we manually closed for reconnecting
    if (event.code === 1000 && event.reason === 'Reconnecting') {
      return false;
    }

    // CRITICAL: Don't reconnect if server replaced our connection (we already have a new one)
    if (event.code === 1000 && event.reason === 'Connection replaced') {
      console.log('Connection was replaced by server (duplicate), not reconnecting');
      return false;
    }

    // Don't reconnect if session was cleaned up
    if (event.code === 1000 && event.reason === 'Session cleanup') {
      return false;
    }

    // Don't reconnect if cleanly closed by server shutdown
    if (event.code === 1001 && event.wasClean) {
      return false;
    }

    // For code 1006 (abnormal closure), reconnect but respect throttle
    if (event.code === 1006) {
      return true;
    }

    // For other non-clean closures, attempt reconnection
    if (!event.wasClean) {
      return true;
    }

    // Clean close with code 1000 but no specific reason (e.g. iOS backgrounding)
    // Reconnect so the user has a working connection when returning to the app
    if (event.code === 1000 && !event.reason) {
      return true;
    }

    return false;
  }

  getDisconnectionReason(event) {
    switch (event.code) {
      case 1000: return 'Normal closure';
      case 1001: return 'Server shutdown';
      case 1003: return 'Unsupported data';
      case 1006: return 'Abnormal closure';
      case 1008: return 'Policy violation (auth error)';
      case 1011: return 'Server error';
      default: return `Unknown code: ${event.code}`;
    }
  }

  categorizeHeartbeatError(error) {
    if (!error.response) {
      return 'NETWORK_ERROR';
    }

    switch (error.response.status) {
      case 401:
        return 'AUTH_ERROR';
      // 403 heisst hier "keine Berechtigung fuer DIESE Session" (fremde Schule, kein
      // Teilnehmer) - nicht "Sitzung abgelaufen". Frueher loeste das den globalen
      // auth:error aus und warf den Nutzer auf die Login-Seite.
      case 403:
      case 404:
        return 'SESSION_NOT_FOUND';
      case 500:
      case 502:
      case 503:
        return 'SERVER_ERROR';
      default:
        return 'UNKNOWN_ERROR';
    }
  }

  handleAuthError(error) {
    this.stopHeartbeat();

    const authErrorEvent = new CustomEvent('auth:error', {
      detail: {
        message: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
        source: 'heartbeat'
      }
    });
    window.dispatchEvent(authErrorEvent);

    this.notifyListeners('authError', {
      message: 'Deine Sitzung ist abgelaufen',
      error: error.response?.status
    });
  }

  getAdaptiveHeartbeatInterval(baseInterval) {
    // Increase interval if we're having connection issues
    if (this.consecutiveFailures > 3) {
      return Math.min(baseInterval * 2, 120000); // Cap at 2 minutes
    }
    return baseInterval;
  }

  adaptHeartbeatInterval(increase) {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      const currentInterval = increase ?
        this.getAdaptiveHeartbeatInterval(60000) :
        (isLocalSession(this.currentSession?.id) ? 60000 : 30000);

      // Restart with new interval
      this.startHeartbeat();
    }
  }

  /**
   * Enhanced session status refresh with better error handling
   */
  async refreshSessionStatus() {
    if (!this.currentSession) {
      console.log('Keine aktive Session zum Aktualisieren');
      return false;
    }

    if (isLocalSession(this.currentSession.id)) {
      console.log('Lokale Session ist immer gültig');
      return true;
    }

    try {
      // Hintergrund-Pruefung: kein globaler Toast. Nach dem Verlassen antwortet
      // das Backend hier zu Recht mit 403 — bis Okt 2026 stand dann auf der
      // Session-Liste „Keine Berechtigung für diese Session“.
      await apiService.getSessionParticipants(this.currentSession.id, { skipErrorToast: true });
      console.log('Session validiert und aktiv');

      // Reset error counters on successful validation
      this.consecutiveFailures = 0;
      this.heartbeatErrors = 0;
      this.lastSuccessfulHeartbeat = Date.now();

      return true;
    } catch (error) {
      console.error('Session ist ungültig oder nicht zugänglich:', error);

      this.consecutiveFailures++;

      // 403 = nicht mehr dabei (verlassen, entfernt) — wie 404 behandeln, so wie
      // der Heartbeat (categorizeHeartbeatError). Sonst blieb die Session gemerkt
      // und jede Pruefung lief erneut in den Fehler.
      if (error.response && [403, 404, 500].includes(error.response.status)) {
        console.warn('Session nicht mehr gültig, wird entfernt');
        this.cleanupSession();
      }

      return false;
    }
  }
}

// Singleton-Instanz exportieren
export default new SessionManager(); 